import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  createEpicorJob,
  listEpicorJobs,
  cancelEpicorJob,
  getEpicorSummary,
  subscribeToEpicorJob,
} from '../../services/epicorApi';

const fmt = (n) => (typeof n === 'number' ? n.toLocaleString() : (n ?? '—'));
const fmtTime = (t) => (t ? new Date(t).toLocaleString() : '—');

const ALL_COMPANIES = ['AU', 'NZ'];

const Counter = ({ label, value, tone }) => (
  <div className={`counter-card counter-${tone || 'neutral'}`}>
    <div className="counter-label">{label}</div>
    <div className="counter-value">{fmt(value || 0)}</div>
  </div>
);

const EpicorSync = () => {
  const [companies, setCompanies] = useState(['AU', 'NZ']);
  const [dryRun, setDryRun] = useState(false);
  const [limit, setLimit] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const [summary, setSummary] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [activeJobId, setActiveJobId] = useState(null);
  const [live, setLive] = useState(null);
  const unsubRef = useRef(null);

  const loadSummary = useCallback(async () => {
    try {
      const data = await getEpicorSummary();
      setSummary(data.companies || []);
    } catch (e) {
      // Non-fatal; summary just stays stale.
    }
  }, []);

  const loadJobs = useCallback(async () => {
    try {
      const data = await listEpicorJobs({ limit: 20 });
      setJobs(data || []);
    } catch (e) {
      // Non-fatal.
    }
  }, []);

  // Poll the job list + summary every 5s, matching JobHistory.js.
  useEffect(() => {
    loadJobs();
    loadSummary();
    const id = setInterval(() => {
      loadJobs();
      loadSummary();
    }, 5000);
    return () => clearInterval(id);
  }, [loadJobs, loadSummary]);

  // Subscribe to the active job's SSE stream for live counters.
  useEffect(() => {
    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }
    setLive(null);
    if (!activeJobId) return undefined;
    const unsub = subscribeToEpicorJob(
      activeJobId,
      (payload) => setLive(payload),
      () => {
        // Stream closed (job finished or timed out) — refresh the list.
        loadJobs();
        loadSummary();
      },
    );
    unsubRef.current = unsub;
    return () => {
      unsub();
      unsubRef.current = null;
    };
  }, [activeJobId, loadJobs, loadSummary]);

  const toggleCompany = (c) => {
    setCompanies((prev) =>
      prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c],
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    if (companies.length === 0) {
      setSubmitError('Select at least one company.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        companies,
        dry_run: dryRun,
        limit: limit ? Number(limit) : null,
      };
      const { job_id } = await createEpicorJob(payload);
      setActiveJobId(job_id);
      await loadJobs();
    } catch (err) {
      setSubmitError(err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const requestCancel = async (id) => {
    if (!window.confirm(`Request cancel for Epicor job #${id}?`)) return;
    try {
      await cancelEpicorJob(id);
      await loadJobs();
    } catch (e) {
      alert(`Cancel failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  // Counters for the active job: prefer the live SSE payload, else the row.
  const activeRow = jobs.find((j) => j.id === activeJobId);
  const merged = { ...(activeRow || {}), ...(live || {}) };
  const counters = merged.counters || {};
  const isRunning = ['pending', 'running'].includes(merged.status);

  return (
    <div className="sync-page epicor-sync">
      <p className="muted">
        Pull the Part master (and custom Short Char / _c fields) from Epicor Kinetic into
        the <code>epicor_part_staging</code> table. Requires the SSH tunnel and a running
        sync worker.
      </p>

      {/* Summary */}
      <section className="card">
        <h3 className="section-h">Staging summary</h3>
        <table>
          <thead>
            <tr>
              <th>Company</th>
              <th>Total</th>
              <th>Present</th>
              <th>Absent</th>
              <th>Last synced</th>
              <th>Last session</th>
            </tr>
          </thead>
          <tbody>
            {summary.length === 0 && (
              <tr><td colSpan={6} className="muted">No staged parts yet.</td></tr>
            )}
            {summary.map((row) => (
              <tr key={row.company}>
                <td><strong>{row.company}</strong></td>
                <td>{fmt(Number(row.total))}</td>
                <td>{fmt(Number(row.present))}</td>
                <td>{fmt(Number(row.absent))}</td>
                <td>{fmtTime(row.last_synced_at)}</td>
                <td className="muted">{row.last_session || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* Trigger */}
      <section className="card">
        <h3 className="section-h">Run a sync</h3>
        <form onSubmit={submit} className="form-grid">
          <div className="form-group">
            <span className="form-label">Companies</span>
            <div className="checkbox-row">
              {ALL_COMPANIES.map((c) => (
                <label key={c} className="chip">
                  <input
                    type="checkbox"
                    checked={companies.includes(c)}
                    onChange={() => toggleCompany(c)}
                  />
                  {c}
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="chip">
              <input
                type="checkbox"
                checked={dryRun}
                onChange={(e) => setDryRun(e.target.checked)}
              />
              Dry run (fetch + count only, no writes)
            </label>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="epicor-limit">
              Limit (optional, per company — disables mark-absent)
            </label>
            <input
              id="epicor-limit"
              type="number"
              min="1"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              placeholder="all"
            />
          </div>

          {submitError && <div className="banner error">{submitError}</div>}

          <div className="actions">
            <button type="submit" disabled={submitting}>
              {submitting ? 'Enqueuing…' : 'Start sync'}
            </button>
          </div>
        </form>
      </section>

      {/* Active job live progress */}
      {activeJobId && (
        <section className="card">
          <h3 className="section-h">
            Active job #{activeJobId}{' '}
            <span className={`status status-${merged.status || 'pending'}`}>
              {merged.status || 'pending'}
            </span>
            {isRunning && (
              <button
                type="button"
                className="link"
                style={{ marginLeft: 12 }}
                onClick={() => requestCancel(activeJobId)}
              >
                Cancel
              </button>
            )}
          </h3>
          <div className="counter-grid">
            <Counter label="Fetched" value={counters.fetched} tone="neutral" />
            <Counter label="Upserted" value={counters.upserted} tone="success" />
            <Counter label="Marked absent" value={counters.marked_absent} tone="warn" />
          </div>
          {merged.error_class && (
            <div className="error job-error">
              <strong>Error ({merged.error_class}):</strong> {merged.error_detail}
            </div>
          )}
        </section>
      )}

      {/* Recent jobs */}
      <section className="card">
        <h3 className="section-h">Recent jobs</h3>
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Status</th>
              <th>Companies</th>
              <th>Dry run</th>
              <th>Fetched</th>
              <th>Upserted</th>
              <th>Absent</th>
              <th>Created by</th>
              <th>Finished</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {jobs.length === 0 && (
              <tr><td colSpan={10} className="muted">No jobs yet.</td></tr>
            )}
            {jobs.map((j) => {
              const c = j.counters || {};
              const comps = (j.payload && j.payload.companies) || [];
              const running = ['pending', 'running'].includes(j.status);
              return (
                <tr key={j.id}>
                  <td>
                    <button type="button" className="link" onClick={() => setActiveJobId(j.id)}>
                      #{j.id}
                    </button>
                  </td>
                  <td><span className={`status status-${j.status}`}>{j.status}</span></td>
                  <td>{Array.isArray(comps) ? comps.join(', ') : '—'}</td>
                  <td>{j.dry_run ? 'yes' : 'no'}</td>
                  <td>{fmt(c.fetched)}</td>
                  <td>{fmt(c.upserted)}</td>
                  <td>{fmt(c.marked_absent)}</td>
                  <td className="muted">{j.created_by}</td>
                  <td className="muted">{fmtTime(j.finished_at)}</td>
                  <td>
                    {running && (
                      <button type="button" className="link" onClick={() => requestCancel(j.id)}>
                        Cancel
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </div>
  );
};

export default EpicorSync;
